import { useEffect } from 'react';

type Lockable = { style: { overflow: string } };

/**
 * A ref-counted scroll lock. The FIRST lock captures the element's original
 * `overflow` and sets `hidden`; nested locks only bump the count; the body is
 * restored to the original value exactly once, when the LAST lock releases —
 * regardless of the order locks are acquired/released in.
 *
 * This is what makes stacked/nested modals safe: the naive per-modal
 * save-and-restore leaks `overflow: hidden` when an inner modal captures the
 * already-locked `hidden` value and restores it last (e.g. a detail modal that
 * closes while its confirm modal is still tearing down), permanently killing page
 * scroll. A shared counter can't be poisoned that way.
 *
 * Exposed as a factory so the ref-counting can be unit-tested without a DOM.
 */
export function createScrollLock() {
  let count = 0;
  let savedOverflow = '';
  return {
    lock(el: Lockable): void {
      if (count === 0) {
        savedOverflow = el.style.overflow;
        el.style.overflow = 'hidden';
      }
      count += 1;
    },
    release(el: Lockable): void {
      if (count === 0) return; // already fully released — never over-restore
      count -= 1;
      if (count === 0) el.style.overflow = savedOverflow;
    },
    /** Current lock depth — for tests/diagnostics. */
    get depth(): number {
      return count;
    },
  };
}

/** The single body-scroll lock shared by every modal. */
const bodyScrollLock = createScrollLock();

/**
 * Lock `document.body` scroll while `active` is true, ref-counted across all
 * callers so stacked modals unlock the body exactly once. Depends only on
 * `active`, so an unstable `onClose`/parent re-render can't re-run it and
 * mis-capture the saved overflow.
 */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    bodyScrollLock.lock(document.body);
    return () => bodyScrollLock.release(document.body);
  }, [active]);
}
